// src/contexts/AuthContext.tsx
import React, { useContext, useEffect, useState, ReactNode, createContext } from "react";
import { auth, db, storage, phoneAuthProvider } from "../firebase/firebase";
import { User, Auth, ParsedToken, multiFactor, PhoneAuthProvider } from "firebase/auth";
import { Firestore } from "firebase/firestore";
import { FirebaseStorage } from "firebase/storage";
import { SKChannel } from "../models/SKChannel";
import { getMyChannel } from "../api/channels/channelServices";
import { useSearchParams } from "react-router-dom";
import { fetchMyInvitations } from "../api/invite/memberServices";
import { MemberInvite } from "../models/ChannelMember";

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
  claims: ParsedToken | undefined;
  myChannel?: SKChannel;
  isMFAUser: boolean;
  refreshAuth: () => Promise<void>;
  userRoles?: UserRole[];
  invitationId?: string;
  myInvites?: MemberInvite[];
  getMyInvites: () => Promise<void>;
  getChannel: () => Promise<void>;
  loading: boolean;
  phoneAuthProvider: PhoneAuthProvider;
  waitForRolesToUpdate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// export type UserRole = "skadmin" | "creator" | "brand" | "";
export type UserClaims = "authenticated" | UserRole;

export enum UserRole {
  CreatorAdmin = "creatoradmin",
  BrandAdmin = "brandadmin",
  Operator = "operator",
  SkarvoAdmin = "skarvoadmin",
  SkarvoContentModerator = "skarvocontentmoderator",
  SkarvoOperator = "skarvooperator",
  SkarvoChannelModerator = "skarvochannelmoderator",
}

export const hasAnyRole = (userRoles: UserRole[], allowedRoles: UserRole[]): boolean => {
  return userRoles.some(item => allowedRoles.includes(item));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  //   if (context === undefined) {
  //     throw new Error("useAuth must be used within an AuthProvider");
  //   }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [myChannel, setChannel] = useState<SKChannel | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState<ParsedToken | undefined>(undefined);
  const [isMFAUser, setIsMFAUser] = useState<boolean>(false);
  const [invitationId, setInvitationId] = useState<string | undefined>(undefined);
  const [myInvites, setMyInvites] = useState<MemberInvite[] | undefined>(undefined);

  const [userRoles, setUserRoles] = useState<UserRole[]>([]);

  // const refreshToken = async () => {
  //   const token = await auth.currentUser?.getIdToken(true);
  // };

  // useEffect(() => {
  //   const unsubscribe = auth.onAuthStateChanged(user => {
  //     if (user) {
  //       getUserRole(user);
  //       setCurrentUser(user);
  //     } else {
  //       setLoading(false);
  //     }
  //   });

  //   return () => {
  //     if (unsubscribe) unsubscribe();
  //   };
  // }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      user ? getUserRole(user) : getUserRole();
      setCurrentUser(user);
      !user && setLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // useEffect(() => {
  //   const invitationId = params.get("invitationId");
  //   if (invitationId) {
  //     setInvitationId(invitationId);
  //   }
  // }, [params]);

  const getChannel = async () => {
    setLoading(true);
    const channelnew = await getMyChannel();
    await refreshAuth();
    if (!channelnew) {
      await getMyInvites();
    } else {
      setChannel(channelnew);
      setMyInvites(undefined);
    }
    setLoading(false);
  };

  useEffect(() => {
    currentUser && getChannel();
  }, [currentUser]);

  // useEffect(() => {
  //   currentUser && refreshAuth();
  // }, [currentUser]);

  const getUserRole = async (user?: User): Promise<UserRole[]> => {
    let result = await (user ? user?.getIdTokenResult(true) : auth.currentUser?.getIdTokenResult(true));
    if (result) {
      let claims = result.claims;
      if (claims) {
        console.log("in AuthContext ", claims);
        setClaims(claims);
        const roles = getUserRolsFromClaim(claims, UserRole); //Object.keys(claims) as UserRole[];
        setUserRoles(roles);
        return roles ?? [];
      }
    }
    return [];
  };

  const getNewRoles = async () => {
    return new Promise<UserRole[]>(resolve => {
      setTimeout(async () => {
        // Simulating an async function returning a random number
        let roles = await getUserRole();
        resolve(roles);
      }, 3000); // Simulate delay of 3 second
    });
  };

  async function waitForRolesToUpdate() {
    let roles: UserRole[] = [];
    do {
      roles = await getNewRoles(); // Call getUserRole to set the userRoles
      // Check the updated userRoles
    } while (!hasAnyRole(roles, [UserRole.Operator, UserRole.SkarvoOperator]));
    // Continue until the targetRole is found

    // console.log(`Target role (${targetRole}) received!`);
  }

  function getUserRolsFromClaim(claims: { [key: string]: any }, enumObj: any): UserRole[] {
    const enumKeys = Object.keys(enumObj).map(key => enumObj[key]);
    return Object.keys(claims).filter(key => enumKeys.includes(key)) as UserRole[];
  }

  // const getUserRole = (claims: ParsedToken): UserRole | null => {
  //   if (claims[UserRole.SkarvoAdmin]) return UserRole.SkarvoAdmin;
  //   if (claims[UserRole.SkarvoContentModerator]) return UserRole.SkarvoContentModerator;
  //   if (claims[UserRole.SkarvoChannelModerator]) return UserRole.SkarvoChannelModerator;
  //   if (claims[UserRole.CreatorAdmin]) return UserRole.CreatorAdmin;
  //   if (claims[UserRole.BrandAdmin]) return UserRole.BrandAdmin;
  //   if (claims[UserRole.Operator]) return UserRole.Operator;
  //   if (claims[UserRole.SkarvoOperator]) return UserRole.SkarvoOperator;
  //   return null;
  // };

  const refreshAuth = async () => {
    if (currentUser) {
      const mfaUsr = await multiFactor(currentUser);
      if (mfaUsr.enrolledFactors.length > 0) {
        setIsMFAUser(true);
      } else {
        setIsMFAUser(false);
      }
    } else {
      setIsMFAUser(false);
    }
  };

  const getMyInvites = async () => {
    const myInvites = await fetchMyInvitations(20);
    setMyInvites(myInvites);
  };

  return (
    <AuthContext.Provider
      value={{
        claims,
        auth,
        db,
        storage,
        currentUser,
        isAuthenticated: !!currentUser,
        myChannel,
        isMFAUser,
        refreshAuth,
        userRoles,
        invitationId,
        myInvites,
        loading,
        phoneAuthProvider,
        getMyInvites,
        getChannel,
        waitForRolesToUpdate,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
